/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * The movements a member's records can point at: their gym's library over the
 * global one (useEffectiveRegistry), falling back to the global listener when
 * the gym read fails, so a gym's own movement shows its name, not its id.
 */
import { useEffect, useMemo } from "react";
import type { MovementRegistryEntry } from "@lemonadetraining/contracts";
import { useEffectiveRegistry } from "./useEffectiveRegistry";
import { useUserAccess } from "./useUserAccess";
import { useMovementRegistryFromContext } from "../contexts/MovementRegistryContext";

export interface UseTrainingRegistryResult {
  status: "loading" | "ready" | "error";
  movements: MovementRegistryEntry[];
}

/** `enabled: false` reads nothing until a surface needs it (the movement search). */
export function useTrainingRegistry({
  enabled = true,
}: { enabled?: boolean } = {}): UseTrainingRegistryResult {
  const { boxID, isLoading } = useUserAccess();
  const effective = useEffectiveRegistry(boxID, { enabled: enabled && !isLoading });
  const global = useMovementRegistryFromContext();
  const enableGlobal = global.enable;
  const fallBack = effective.status === "error";

  useEffect(() => {
    if (fallBack) enableGlobal();
  }, [fallBack, enableGlobal]);

  return useMemo(() => {
    if (!fallBack) {
      return {
        status: effective.status === "ready" ? "ready" : "loading",
        movements: effective.movements,
      };
    }
    if (global.status === "ready") return { status: "ready", movements: global.movements };
    return { status: global.status === "error" ? "error" : "loading", movements: [] };
  }, [fallBack, effective.status, effective.movements, global.status, global.movements]);
}
